import isEqual from 'lodash/isEqual';
import { createSelector, createStructuredSelector } from 'reselect';

import { SMALL_BUSINESS_SHORT } from '../../constants/domain-specific';
import { Cover } from '../../types/cover';
import { PaymentFrequencyOptions } from '../../types/payments';
import { InterestedPartyUI, Payment, PolicyBuilderMode, PolicyRequest } from '../../types/policy';
import { utcDateFormatter } from '../../utils';
import { selectEnhancedExtraInfo } from '../index';
import { selectAppDetails } from '../selectAppDetails';
import { selectBusinessDetails, selectIsPropertyOwner } from '../selectBusinessDetails';
import { selectCoverDetails } from '../selectCoverDetails';
import { selectOrderedSelectCover } from '../selectCoverOptions';
import { selectPayments } from '../selectPayment';
import { selectPropertyOwnerOccupation } from '../selectPropertyOwnerOccupation';
import { selectQuote } from '../selectQuote';
import { getBusinessLocation } from './getBusinessLocation';
import { getCustomerParty } from './getCustomerParty';
import { getPayment } from './getPayment';
import { getPolicyHeader } from './getPolicyHeader';
import { getQuestions } from './getQuestions';
import { getRiskMeta } from './getRiskMeta';
import { getUniqueRiskList } from './getUniqueRiskList';

export interface PolicyDataBuilderOptions {
  /** @todo infer issueDocument, includeAccessToken and makePayment from the new "mode" attribute */
  mode?: PolicyBuilderMode;
  covers?: Cover[];
  interestedParties?: InterestedPartyUI[];
  issueDocument?: boolean;
  includeAccessToken?: boolean;
  makePayment?: boolean;
}

const optionsSelector = createSelector(
  [(_, options?: PolicyDataBuilderOptions) => options],
  (options) => options,
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  }
);

const selectPolicyDataOptions = createStructuredSelector({
  orderedCovers: selectOrderedSelectCover,
  coverDetails: selectCoverDetails,
  businessDetails: selectBusinessDetails,
  quote: selectQuote,
  extraInfo: selectEnhancedExtraInfo,
  payments: selectPayments,
  isPropertyOwner: selectIsPropertyOwner,
  propertyOwnerOccupation: selectPropertyOwnerOccupation,
  appDetails: selectAppDetails,
});

export const selectPolicyData = createSelector(
  [selectPolicyDataOptions, optionsSelector],
  (
    {
      orderedCovers,
      coverDetails,
      businessDetails,
      quote,
      extraInfo,
      payments,
      isPropertyOwner,
      propertyOwnerOccupation,
      appDetails,
    },
    options = {}
  ): PolicyRequest => {
    const {
      covers: customCovers,
      interestedParties: customInterestedParties,
      issueDocument = false,
      makePayment = false,
    } = options;
    const { coverStart } = businessDetails;
    const { stampdutyExempted } = coverDetails;
    const { quoteNumber, premiums } = quote;
    const { validatedCovers } = orderedCovers;

    const requestCovers = [...(customCovers ?? validatedCovers)];

    const premium = premiums?.policy?.policyPremium?.transactionPremium?.totalPremium;
    const coverStartFormatted = (coverStart && utcDateFormatter(coverStart)) ?? '';

    const businessLocation = getBusinessLocation({ businessDetails });

    const riskMeta = getRiskMeta({
      coverStartFormatted,
      businessLocation,
      businessDetails,
    });

    const customerParty = getCustomerParty({
      extraInfo,
      includeAccessToken: !!options.includeAccessToken,
      businessLocation,
      quote,
      appDetails,
    });

    const payment: Payment = getPayment({
      payments,
      premium,
    });

    const uniqueRiskList = getUniqueRiskList({
      riskMeta,
      coverDetails,
      extraInfo,
      isPropertyOwner,
      businessLocation,
      businessDetails,
      quote,
      requestCovers,
      interestedParties: customInterestedParties,
      propertyOwnerOccupation,
    });

    return {
      policy: {
        policyHeader: {
          ...getPolicyHeader({
            coverStartFormatted,
            quoteNumber,
          }),
          issueDocument,
        },
        customerParty,
        risks: uniqueRiskList,
        questions: getQuestions({ extraInfo }),
        policyPremium: {
          stampDutyExemptIndicator: stampdutyExempted ? SMALL_BUSINESS_SHORT : '',
        },
        ...(makePayment
          ? { payment }
          : /* We default to monthly to allow MS to return the monthly instalments */
            { payment: { paymentPeriod: PaymentFrequencyOptions.Monthly } }),
      },
    };
  }
);
